// =========================================================
// frontend/src/pages/WithdrawCoinsConfirm.js
// =========================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/WithdrawCoinsConfirm.css";

export default function WithdrawCoinsConfirm() {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
  const userEmail = currentUser?.email;

  // ✅ Taux EVO LIVE (modifiable)
  const RATE_COINS_PER_EURO = 100; // 100 coins = 1€
  const MIN_WITHDRAW_COINS = 100; // exemple: minimum 100 coins

  const [loading, setLoading] = useState(true);
  const [coins, setCoins] = useState(0);

  const [acceptRules, setAcceptRules] = useState(false);
  const [acceptIrreversible, setAcceptIrreversible] = useState(false);
  const [acceptAccuracy, setAcceptAccuracy] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // =========================================
  // ✅ Calcul du montant en €
  // =========================================
  const euros = useMemo(() => {
    const value = Number(coins || 0) / RATE_COINS_PER_EURO;
    // Affichage au centime
    return Math.floor(value * 100) / 100;
  }, [coins]);

  const canSubmit = useMemo(() => {
    return (
      !loading &&
      !submitting &&
      userEmail &&
      Number(coins) >= MIN_WITHDRAW_COINS &&
      acceptRules &&
      acceptIrreversible &&
      acceptAccuracy
    );
  }, [
    loading,
    submitting,
    userEmail,
    coins,
    MIN_WITHDRAW_COINS,
    acceptRules,
    acceptIrreversible,
    acceptAccuracy,
  ]);

  // =========================================
  // ✅ Charger le solde coins
  // =========================================
  useEffect(() => {
    let mounted = true;

    async function fetchBalance() {
      try {
        setLoading(true);
        setErrorMsg("");

        if (!userEmail) {
          setCoins(0);
          setLoading(false);
          return;
        }

        // ✅ Ton endpoint existe déjà chez toi : /api/coins/balance/:email
        const res = await axios.get(`/api/coins/balance/${encodeURIComponent(userEmail)}`);
        const balance = Number(res?.data?.coins ?? res?.data?.balance ?? 0);

        if (mounted) {
          setCoins(balance);
        }
      } catch (err) {
        console.error("❌ Erreur récupération solde coins:", err);
        if (mounted) {
          setErrorMsg("Impossible de récupérer votre solde coins pour le moment.");
          setCoins(0);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchBalance();
    return () => {
      mounted = false;
    };
  }, [userEmail]);

  // =========================================
  // ✅ Submit conversion (backend à faire ensuite)
  // =========================================
  async function handleConfirm() {
    try {
      setSubmitting(true);
      setErrorMsg("");

      if (!userEmail) {
        setErrorMsg("Vous devez être connecté pour effectuer une conversion.");
        setSubmitting(false);
        return;
      }

      if (Number(coins) < MIN_WITHDRAW_COINS) {
        setErrorMsg(
          `Minimum requis : ${MIN_WITHDRAW_COINS} coins. Votre solde actuel : ${coins} coins.`
        );
        setSubmitting(false);
        return;
      }

      // ✅ Endpoint backend à faire ensuite (étape B)
      // - convertit coins -> euros
      // - met coins à 0
      // - crédite euros
      // - log historique + trace acceptation
      const payload = {
        email: userEmail,
        acceptRules: true,
        acceptIrreversible: true,
        acceptAccuracy: true,
        rate: RATE_COINS_PER_EURO,
      };

      await axios.post("/api/coins/convert-to-euros", payload);

      // ✅ Redirection vers la page solde (où euros doit apparaître)
      navigate("/solde");
    } catch (err) {
      console.error("❌ Erreur conversion coins -> euros:", err);
      const msg =
        err?.response?.data?.error ||
        "Conversion impossible pour le moment. Réessayez dans quelques instants.";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // =========================================
  // UI
  // =========================================
  return (
    <div className="withdraw-confirm-page">
      <div className="withdraw-confirm-header">
        <div className="withdraw-confirm-title">
          💶 Conversion coins → euros
        </div>
        <button className="withdraw-confirm-back" onClick={() => navigate(-1)}>
          ← Retour
        </button>
      </div>

      <div className="withdraw-confirm-subtitle">
        Convertissez vos coins en euros pour demander un retrait.
      </div>

      {!userEmail && (
        <div className="withdraw-confirm-alert">
          Vous devez être connecté pour accéder à la conversion.
        </div>
      )}

      {errorMsg && <div className="withdraw-confirm-error">{errorMsg}</div>}

      <div className="withdraw-confirm-grid">
        <div className="withdraw-confirm-card">
          <div className="card-title">🪙 Solde coins</div>
          <div className="card-value">
            {loading ? "..." : `${Number(coins || 0)} coins`}
          </div>
          <div className="card-sub">
            Minimum conversion : {MIN_WITHDRAW_COINS} coins
          </div>
        </div>

        <div className="withdraw-confirm-card">
          <div className="card-title">📈 Taux EVO LIVE</div>
          <div className="card-value">{RATE_COINS_PER_EURO} coins = 1 €</div>
          <div className="card-sub">Le taux peut évoluer selon EVO LIVE.</div>
        </div>

        <div className="withdraw-confirm-card highlight">
          <div className="card-title">💶 Montant estimé</div>
          <div className="card-value">
            {loading ? "..." : `${euros.toFixed(2)} €`}
          </div>
          <div className="card-sub">
            Basé sur votre solde coins actuel.
          </div>
        </div>
      </div>

      <div className="withdraw-confirm-legal">
        <div className="legal-title">📄 Conditions de conversion et retrait</div>

        <div className="legal-text">
          <p>
            En confirmant cette conversion, vous acceptez que :
          </p>
          <ul>
            <li>
              La conversion coins → euros est <b>irréversible</b> une fois validée.
            </li>
            <li>
              Les euros crédités sont destinés à un <b>retrait</b> ou à une
              <b> reconversion</b> selon les règles EVO LIVE.
            </li>
            <li>
              Vous êtes responsable de fournir des informations exactes (coordonnées,
              méthode de paiement, identité si demandée). Toute erreur peut entraîner
              un <b>retard</b>, un <b>refus</b> ou des frais selon la réglementation.
            </li>
            <li>
              EVO LIVE peut appliquer des mesures de sécurité (vérification, seuils,
              anti-fraude) et se réserve le droit de bloquer une opération en cas
              d’activité suspecte.
            </li>
            <li>
              Les conversions et demandes de retrait peuvent être soumises à des
              exigences légales (KYC/AML, âge minimum, vérifications).
            </li>
          </ul>

          <p className="legal-note">
            ⚠️ Les gains affichés tiennent compte de la commission EVO (20 %).
          </p>

          <p className="legal-note">
            ⚖️ Ce texte est une base “conditions internes”. On pourra l’améliorer
            plus tard avec des liens vers tes CGU/Politique de confidentialité
            et un vrai module de conformité (KYC/AML).
          </p>
        </div>

        <div className="legal-checkboxes">
          <label className="check-line">
            <input
              type="checkbox"
              checked={acceptRules}
              onChange={(e) => setAcceptRules(e.target.checked)}
            />
            <span>J’ai lu et j’accepte les conditions de conversion et retrait.</span>
          </label>

          <label className="check-line">
            <input
              type="checkbox"
              checked={acceptIrreversible}
              onChange={(e) => setAcceptIrreversible(e.target.checked)}
            />
            <span>Je comprends que la conversion est irréversible après validation.</span>
          </label>

          <label className="check-line">
            <input
              type="checkbox"
              checked={acceptAccuracy}
              onChange={(e) => setAcceptAccuracy(e.target.checked)}
            />
            <span>
              Je certifie que mes informations de retrait seront exactes et à jour.
            </span>
          </label>
        </div>

        <div className="withdraw-confirm-actions">
          <button
            className="btn-cancel"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Annuler
          </button>

          <button
            className="btn-confirm"
            onClick={handleConfirm}
            disabled={!canSubmit}
            title={
              !canSubmit
                ? "Veuillez cocher les cases et vérifier le minimum coins."
                : "Valider la conversion"
            }
          >
            {submitting ? "Conversion..." : "✅ Valider la conversion"}
          </button>
        </div>
      </div>
    </div>
  );
}
